import { lazyAppContext } from '@1inch-community/core/lazy';
import { objectsEqual } from '@1inch-community/core/utils';
import { BehaviorSubject, debounceTime, defaultIfEmpty, first, fromEvent, Subject, take, takeUntil, tap, timer, } from 'rxjs';
import { adapterId } from './adapter-id';
import { UniversalBrowserExtensionAdapter } from './adapters/universal-browser-extension-adapter';
import { getWalletConnectProviderDetail, WalletConnectV2Adapter, } from './adapters/wallet-connect-v2-adapter';
import { GlobalDataAdapter } from './global-data-adapter';
import { getInjectedProviderDetail, getInjectedProviderSupported } from './injected-provider-detail';
import { addConnectedWallet, getActiveAddress, getActiveWallet, getChainIdsFromStorage, getConnectedWallet, removeConnectedWallet, setActiveWallet, } from './storage';
export class WalletController {
    constructor() {
        this.context = lazyAppContext('WalletController');
        this.data = new GlobalDataAdapter(this);
        this.activeAdapters = new Map();
        this.update$ = new Subject();
        this.supportedWallets$ = new BehaviorSubject([]);
        this.connectionUriLink$ = new Subject();
        this.currentActiveAdapterId = null;
        this.adapters = new Map();
    }
    get isConnected() {
        return this.currentActiveAdapter !== null;
    }
    get connectedWalletInfo() {
        return this.currentActiveAdapter?.info ?? null;
    }
    get currentActiveAdapter() {
        if (!this.currentActiveAdapterId)
            return null;
        return this.activeAdapters.get(this.currentActiveAdapterId) ?? null;
    }
    async init(context) {
        this.context.set(context);
        // todo: use Promise.all for app start speed up
        await this.data.init(context);
        await this.initWallets();
        this.restoreChainId();
        await this.restoreWalletConnection();
        this.update$.pipe(tap(() => this.updateSupportWallets())).subscribe();
        this.update$.next();
    }
    async getSupportedWallets() {
        return this.supportedWallets$.value;
    }
    async connect(info, opts) {
        const chainId = await this.data.getChainId();
        const id = adapterId(info);
        if (!this.adapters.has(id)) {
            throw new Error(`Invalid wallet info ${info.name} not exist`);
        }
        const connectState = await this.connectSafe(chainId, id, opts);
        this.afterConnectWallet(connectState, id);
        this.update$.next();
        return connectState;
    }
    async addConnection(info, opts) {
        const chainId = await this.data.getChainId();
        const id = adapterId(info);
        if (!this.adapters.has(id)) {
            throw new Error(`Invalid wallet info ${info.name} not exist`);
        }
        const adapter = this.adapters.get(id);
        if (!adapter) {
            throw new Error(`Invalid wallet id`);
        }
        if (!this.activeAdapters.has(id)) {
            throw new Error(`Wallet adapter ${info.name} not connected`);
        }
        let connectState;
        try {
            connectState = await this.adapterConnect(adapter, chainId, opts);
        }
        catch {
            connectState = false;
        }
        this.update$.next();
        return connectState;
    }
    async disconnectAllWallets() {
        if (this.currentActiveAdapterId) {
            removeConnectedWallet(this.context.value.storage, this.currentActiveAdapterId);
        }
        for (const [id, adapter] of this.adapters) {
            const isWalletConnect = adapter.info.uuid === 'walletConnect';
            await adapter.disconnect();
            if (!isWalletConnect) {
                this.activeAdapters.delete(id);
            }
        }
        setActiveWallet(this.context.value.storage, null);
        this.currentActiveAdapterId = null;
        this.update$.next();
        return true;
    }
    async disconnect(info, address) {
        if (!info) {
            return await this.disconnectAllWallets();
        }
        const id = adapterId(info);
        const adapter = this.adapters.get(id);
        if (!adapter)
            return true;
        try {
            const isWalletConnect = adapter.info.uuid === 'walletConnect';
            const state = await adapter.disconnect(address);
            const addresses = await adapter.data.getAddresses();
            const hasConnectedAddresses = addresses.length > 0;
            if (this.currentActiveAdapterId) {
                if (!hasConnectedAddresses) {
                    removeConnectedWallet(this.context.value.storage, this.currentActiveAdapterId);
                    setActiveWallet(this.context.value.storage, null);
                }
                if (!isWalletConnect) {
                    this.activeAdapters.delete(this.currentActiveAdapterId);
                    this.currentActiveAdapterId = null;
                }
                await this.restoreWalletConnection();
            }
            this.update$.next();
            return state;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    setChainIds(chainIds) {
        this.data.setChainIds(chainIds);
    }
    getDataAdapter(info) {
        const id = adapterId(info);
        const adapter = this.adapters.get(id);
        if (!adapter) {
            throw new Error(`Invalid wallet info ${info.name} not exist`);
        }
        return adapter.data;
    }
    async setActiveAddress(info, address) {
        const id = adapterId(info);
        return await this.setActiveAddressInner(id, address);
    }
    async writeContract(params) {
        if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
            throw new Error('Wallet not connected');
        }
        return await this.currentActiveAdapter.writeContract(params);
    }
    async signTypedData(typeData) {
        if (!this.currentActiveAdapter || !this.currentActiveAdapter.client) {
            throw new Error('Wallet not connected');
        }
        return await this.currentActiveAdapter.signTypedData(typeData);
    }
    connectionUriLink() {
        return this.connectionUriLink$.asObservable();
    }
    async isSupportConnectionUriLink(info) {
        const id = adapterId(info);
        const support = await this.adapters.get(id)?.isSupportConnectionUriLink();
        return support ?? false;
    }
    async setActiveAddressInner(id, address) {
        let state = true;
        if (this.currentActiveAdapterId !== id) {
            const chainId = await this.data.getChainId();
            if (!this.adapters.has(id)) {
                throw new Error(`Invalid wallet not exist`);
            }
            state = await this.connectSafe(chainId, id);
            this.afterConnectWallet(state, id);
        }
        if (state) {
            this.currentActiveAdapter?.setActiveAddress(address);
        }
        this.update$.next();
    }
    async connectSafe(chainId, walletId, opts, retry = false) {
        const adapter = this.adapters.get(walletId);
        if (!adapter) {
            throw new Error(`Invalid wallet id`);
        }
        let connectState;
        if (!this.activeAdapters.has(walletId) || retry) {
            try {
                connectState = await this.adapterConnect(adapter, chainId, opts);
            }
            catch {
                connectState = false;
            }
            connectState && this.activeAdapters.set(walletId, adapter);
        }
        else {
            connectState = await adapter.isConnected();
            if (!retry && !connectState) {
                connectState = await this.connectSafe(chainId, walletId, opts, true);
            }
        }
        return connectState;
    }
    async adapterConnect(adapter, chainId, opts) {
        adapter
            .connectionUriLink()
            .pipe(first(), tap((uri) => this.connectionUriLink$.next(uri)))
            .subscribe();
        return await adapter.connect(chainId, opts);
    }
    async restoreConnectSafe(chainId, walletId, force) {
        const adapter = this.adapters.get(walletId);
        if (!adapter) {
            throw new Error(`Invalid wallet id`);
        }
        let connectState;
        if (!this.activeAdapters.has(walletId)) {
            try {
                connectState = await adapter.restoreConnect(chainId, force);
            }
            catch {
                connectState = false;
            }
            connectState && this.activeAdapters.set(walletId, adapter);
        }
        else {
            connectState = await adapter.isConnected();
        }
        return connectState;
    }
    restoreChainId() {
        const chainIds = getChainIdsFromStorage(this.context.value.storage);
        if (!chainIds)
            return;
        this.setChainIds(chainIds);
    }
    async restoreWalletConnection() {
        const activeWalletId = getActiveWallet(this.context.value.storage);
        const connectedWallet = getConnectedWallet(this.context.value.storage);
        if (!connectedWallet)
            return;
        const chainId = await this.data.getChainId();
        await this.restoreWalletConnectionNotActiveWallet(chainId, activeWalletId, connectedWallet);
        await this.restoreWalletConnectionActiveWallet(chainId, activeWalletId);
    }
    async restoreWalletConnectionNotActiveWallet(chainId, activeWalletId, connectedWallet) {
        for (const id of connectedWallet) {
            if (id === activeWalletId)
                continue;
            if (!this.adapters.has(id))
                continue;
            const connectState = await this.restoreConnectSafe(chainId, id, false).catch(() => false);
            this.afterConnectWallet(connectState, id);
        }
    }
    async restoreWalletConnectionActiveWallet(chainId, activeWalletId) {
        if (!activeWalletId || !this.adapters.has(activeWalletId))
            return;
        const connectState = await this.restoreConnectSafe(chainId, activeWalletId, true);
        this.afterConnectWallet(connectState, activeWalletId);
        if (!connectState)
            return;
        const activeAddressFromStore = getActiveAddress(this.context.value.storage);
        if (!activeAddressFromStore || !this.currentActiveAdapter)
            return;
        const addresses = await this.currentActiveAdapter.data.getAddresses();
        if (!addresses.includes(activeAddressFromStore))
            return;
        await this.setActiveAddressInner(activeWalletId, activeAddressFromStore);
    }
    initWallets() {
        return new Promise((resolve) => {
            let skipInjectedProvider = false;
            fromEvent(window, 'eip6963:announceProvider')
                .pipe(tap((event) => {
                skipInjectedProvider = objectsEqual(window.ethereum, event.detail.provider);
                const id = adapterId(event.detail.info);
                if (!this.adapters.has(id)) {
                    this.adapters.set(id, new UniversalBrowserExtensionAdapter(event.detail));
                }
            }), debounceTime(100), take(1), takeUntil(timer(100)), defaultIfEmpty(null), tap(async () => {
                if (!skipInjectedProvider && getInjectedProviderSupported()) {
                    const injectedProviderDetail = await getInjectedProviderDetail();
                    const id = adapterId(injectedProviderDetail.info);
                    if (!this.adapters.has(id)) {
                        this.adapters.set(id, new UniversalBrowserExtensionAdapter(injectedProviderDetail));
                    }
                }
                const wc = await getWalletConnectProviderDetail();
                const wcId = adapterId(wc.info);
                this.adapters.set(wcId, new WalletConnectV2Adapter(wc));
                resolve();
            }))
                .subscribe();
            window.dispatchEvent(new Event('eip6963:requestProvider'));
        });
    }
    afterConnectWallet(connectState, id) {
        if (connectState) {
            this.currentActiveAdapterId = id;
            addConnectedWallet(this.context.value.storage, id);
            setActiveWallet(this.context.value.storage, id);
        }
        else {
            removeConnectedWallet(this.context.value.storage, id);
            const currentActiveWalletIdFromStore = getActiveWallet(this.context.value.storage);
            if (currentActiveWalletIdFromStore === id) {
                setActiveWallet(this.context.value.storage, null);
            }
            if (this.currentActiveAdapterId === id) {
                this.currentActiveAdapterId = null;
            }
            if (this.activeAdapters.has(id)) {
                const adapter = this.adapters.get(id);
                adapter.disconnect().catch();
                this.activeAdapters.delete(id);
            }
        }
    }
    updateSupportWallets() {
        const info = [];
        const equal = (a, b) => {
            if (a.length !== b.length)
                return false;
            return a.every((val, index) => val.uuid === b[index].uuid);
        };
        this.adapters.forEach((adapter) => info.push(adapter.data.getInfo()));
        const sortedAdapters = info.sort((info1, info2) => {
            const id1 = adapterId(info1);
            const id2 = adapterId(info2);
            if (id1 === this.currentActiveAdapterId && id2 !== this.currentActiveAdapterId) {
                return -1;
            }
            if (id1 !== this.currentActiveAdapterId && id2 === this.currentActiveAdapterId) {
                return 1;
            }
            if (this.activeAdapters.has(id1) && this.activeAdapters.has(id2)) {
                return 0;
            }
            if (this.activeAdapters.has(id1) && !this.activeAdapters.has(id2)) {
                return -1;
            }
            if (!this.activeAdapters.has(id1) && this.activeAdapters.has(id2)) {
                return 1;
            }
            return 0;
        });
        if (!equal(this.supportedWallets$.value, sortedAdapters)) {
            this.supportedWallets$.next(sortedAdapters);
        }
    }
}
//# sourceMappingURL=connect-wallet-controller.js.map